import React, { useEffect, useRef, useState } from "react";
import { Share2, Download, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { copyToClipboard, shareCardImage } from "@/lib/share";

const W = 1200, H = 630;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function drawCard(canvas, pick, strategyLabel, site) {
  const fonts = Promise.all([
    document.fonts.load("300 90px Cormorant"),
    document.fonts.load("italic 400 44px Cormorant"),
    document.fonts.load("500 22px 'JetBrains Mono'"),
  ]);
  await Promise.race([fonts, new Promise((r) => setTimeout(r, 2500))]).catch(() => {});
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = "#06070A"; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W * 0.72, H * 0.45, 20, W * 0.72, H * 0.45, 520);
  glow.addColorStop(0, "rgba(255,176,0,0.22)"); glow.addColorStop(1, "rgba(255,176,0,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255,255,255,0.05)"; ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
  roundRect(ctx, 40, 40, W - 80, H - 80, 24);
  ctx.strokeStyle = "rgba(255,176,0,0.35)"; ctx.lineWidth = 2; ctx.stroke();

  ctx.fillStyle = "#FFB000"; ctx.fillRect(96, 96, 60, 3);
  ctx.font = "500 20px 'JetBrains Mono', monospace"; ctx.fillStyle = "#FFB000"; ctx.textBaseline = "top";
  ctx.letterSpacing = "5px";
  ctx.fillText(`POWERBALL ORACLE · ${strategyLabel.toUpperCase()}`, 96, 120);
  ctx.letterSpacing = "0px";
  ctx.font = "300 88px Cormorant, Georgia, serif"; ctx.fillStyle = "#F4F0E6";
  ctx.fillText("The numbers", 96, 160);
  ctx.font = "italic 400 88px Cormorant, Georgia, serif"; ctx.fillStyle = "#FFB000";
  ctx.fillText("have spoken.", 96, 250);

  const r = 52, gap = 24, y = 440;
  let x = 96 + r;
  const balls = [...pick.whites.map((n) => ({ n, red: false })), { n: pick.powerball, red: true }];
  balls.forEach((b, i) => {
    if (i === 5) x += 20;
    ctx.save();
    ctx.shadowColor = b.red ? "rgba(229,9,20,0.7)" : "rgba(255,255,255,0.35)"; ctx.shadowBlur = 30;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
    const g = ctx.createRadialGradient(x - 18, y - 18, 6, x, y, r);
    if (b.red) { g.addColorStop(0, "#ff5a5f"); g.addColorStop(1, "#b3000a"); } else { g.addColorStop(0, "#ffffff"); g.addColorStop(1, "#d9d4c7"); }
    ctx.fillStyle = g; ctx.fill(); ctx.restore();
    ctx.font = "600 40px 'JetBrains Mono', monospace"; ctx.fillStyle = b.red ? "#fff" : "#06070A";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(String(b.n).padStart(2, "0"), x, y + 2);
    ctx.textAlign = "left"; ctx.textBaseline = "top";
    x += r * 2 + gap;
  });

  ctx.font = "500 18px 'JetBrains Mono', monospace"; ctx.fillStyle = "rgba(244,240,230,0.55)"; ctx.letterSpacing = "3px";
  ctx.fillText(`CONFIDENCE ${(pick.confidence * 100).toFixed(1)}%   ·   ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase()}`, 96, 516);
  ctx.fillStyle = "#FFB000";
  ctx.fillText(site.toUpperCase(), 96, 546);
  ctx.letterSpacing = "0px";
  ctx.font = "400 14px 'JetBrains Mono', monospace"; ctx.fillStyle = "rgba(244,240,230,0.3)";
  ctx.fillText("Powerball draws are random · Play responsibly", 96, 574);
}

function CardCanvas({ pick, strategyLabel, site, onReady }) {
  const ref = useRef(null);
  useEffect(() => {
    let alive = true;
    drawCard(ref.current, pick, strategyLabel, site).then(() => alive && onReady(ref.current.toDataURL("image/png")));
    return () => { alive = false; };
  }, [pick, strategyLabel, site, onReady]);
  return <canvas ref={ref} className="hidden" />;
}

export function SharePick({ pick, strategyLabel, open, onOpenChange }) {
  const [dataUrl, setDataUrl] = useState("");
  const site = window.location.host;
  const url = window.location.origin;
  const text = `My Powerball Oracle pick (${strategyLabel}): ${pick.whites.join(" · ")} + PB ${pick.powerball}. Get yours →`;

  useEffect(() => { if (!open) setDataUrl(""); }, [open]);

  const filename = `powerball-oracle-${pick.whites.join("-")}-pb${pick.powerball}.png`;

  const download = () => {
    const a = document.createElement("a"); a.href = dataUrl; a.download = filename; a.click();
    toast.success("Card saved");
  };
  const nativeShare = () => shareCardImage(dataUrl, filename, text, url);
  const copyText = () => copyToClipboard(`${text} ${url}`, "Caption copied — attach the card image when you post");

  const enc = encodeURIComponent;
  const links = [
    { name: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}` },
    { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${enc(text + " " + url)}` },
    { name: "Telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}` },
    { name: "Reddit", href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(text)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-pick-dialog" className="bg-surface border-white/10 text-parchment max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Share your <span className="italic text-oracle">pick</span></DialogTitle>
        </DialogHeader>
        {open && <CardCanvas pick={pick} strategyLabel={strategyLabel} site={site} onReady={setDataUrl} />}
        {dataUrl ? (
          <img data-testid="share-card-preview" src={dataUrl} alt="Your Powerball Oracle pick card" className="w-full rounded-lg border border-oracle/20" />
        ) : (
          <div className="aspect-[1200/630] w-full rounded-lg bg-black/40 label-caps flex items-center justify-center">Rendering card…</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="share-download-btn" onClick={download} disabled={!dataUrl} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps"><Download className="w-4 h-4 mr-2" /> Download PNG</Button>
          {navigator.share && (
            <Button data-testid="share-native-btn" onClick={nativeShare} disabled={!dataUrl} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><Share2 className="w-4 h-4 mr-2" /> Share…</Button>
          )}
          <Button data-testid="share-copy-btn" onClick={copyText} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><Copy className="w-4 h-4 mr-2" /> Copy caption</Button>
        </div>
        <div className="grid grid-cols-5 gap-2" data-testid="share-social-links">
          {links.map((l) => (
            <a key={l.name} href={l.href} target="_blank" rel="noreferrer" data-testid={`share-pick-${l.name.toLowerCase()}`} className="label-caps text-center text-parchment/80 hover:text-oracle border border-white/10 hover:border-oracle/40 rounded-md py-2.5 transition-colors">{l.name}</a>
          ))}
        </div>
        <p className="font-body text-[11px] text-parchment/40">Social sites don't accept images via link — download the card, then attach it to your post with the copied caption. On mobile, "Share…" sends the image directly.</p>
      </DialogContent>
    </Dialog>
  );
}
